import React, { useState, useEffect } from "react";
import { OI_URL } from "../../config/config";
import { Modal, Button, Form, Input, Select, Table, Tag, Space, Alert, Tabs } from "antd";
import { PlusOutlined } from '@ant-design/icons';
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import PermissionsRequired from "../../helper/PermissionsRequired";
import moment from "moment";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const MerchantModal = (props) => {
    const { t, merchantId, isModalVisible, handleCancel, fetchMerchants } = props;
    const [merchant, setMerchant] = useState(null);
    const [loading, setLoading] = useState(false);
    const [providerList, setProviderList] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [serviceNames, setServiceNames] = useState({});
    const [clientLegalName, setClientLegalName] = useState(""); 
    const [rdrRulesData, setRdrRulesData] = useState([]);
    const [isRdrRules, setIsRdrRules] = useState(false);
    const [rdrRulesTotal, setRdrRulesTotal] = useState(0);
    const [rdrRulesPage, setRdrRulesPage] = useState(1);
    const [rdrRulesPageSize, setRdrRulesPageSize] = useState(10);
    
    // const [editingEnrollment, setEditingEnrollment] = useState(null);
    // const [editLegalName, setEditLegalName] = useState("");

    // This tracks the original active enrollments when the modal is first opened
    const [originalEnrollments, setOriginalEnrollments] = useState([]);

    const [form] = Form.useForm();

    const isReadOnly =  true; //!PermissionsRequired("SYSADMIN");

    useEffect(() => {
        if (merchantId) {
            setOriginalEnrollments([]);
            setRdrRulesData([]);
            setRdrRulesPage(1);
            setRdrRulesPageSize(10);
            getMerchantDetail();
        }
        if (!providerList.length) {
            getProviderList();
        }
    }, [merchantId]); // eslint-disable-line react-hooks/exhaustive-deps

    const getMerchantDetail = async () => {
        setLoading(true);
        const apiUrl = OI_URL + `/api/v1/txshield/merchant/${merchantId}/details/`;
        try {
            const { data } = await axios.post(apiUrl, "", { 
                headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } 
            });
            
            if (data.enrollments) {
                data.enrollments = data.enrollments.map(enrollment => ({
                    ...enrollment,
                    client_legal_name: enrollment.client_legal_name || null
                }));
                
                // Only update originalEnrollments when the modal is initially opened (not during refresh)
                if (originalEnrollments.length === 0) {
                    const activeEnrollments = data.enrollments.filter(e => e.active === true);
                    // Create a deep copy to prevent reference issues
                    setOriginalEnrollments(JSON.parse(JSON.stringify(activeEnrollments)));
                }
            }
            
            setMerchant(data);
            form.setFieldsValue({
                merchantId: data.merchantId,
                name: data.name,
                jwtToken: data.jwtToken,
                verifiMerchantName: data.verifiMerchantName,
                verifiMid: data.verifiMid,
                enrollmentStatus: data.enrollmentStatus,
                active: data.active
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("login.dialogs.catch"),
                text: error.response ? error.response.data.msg : "",
                showConfirmButton: true,
            });
        }
        setLoading(false);
    };

    const getProviderList = async () => {
        const apiUrl = OI_URL + `/api/v1/txshield/providers`;
        try {
            const { data } = await axios.post(apiUrl, "", { 
                headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } 
            });
            setProviderList(data);
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("login.dialogs.catch"),
                text: error.response ? error.response.data.msg : "",
            });
        }
    };

    const generateJWTToken = async () => {
        const apiUrl = OI_URL + `/api/v1/txshield/merchant/${merchantId}/generateJWTToken`;
        try {
            const { data } = await axios.post(apiUrl, "", { 
                headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } 
            });
            if (data.status === "ok") {
                setMerchant({ ...merchant, jwtToken: data.token });
                form.setFieldsValue({ ...merchant, jwtToken: data.token });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("login.dialogs.catch"),
                text: error.response ? error.response.data.msg : "",
                showConfirmButton: true,
            });
        }
    };

    const handleOk = () => {
        const hasActiveEnrollment = merchant?.enrollments?.some(enrollment => enrollment.active === true);
        
        if (!hasActiveEnrollment) {
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "At least one active service enrollment is required",
                showConfirmButton: true,
            });
            return;
        }
        
        setLoading(true);
        form.submit();
    };

    const handleCancelWithRestore = async () => {
        try {
            if (originalEnrollments.length > 0) {
                const allCurrentEnrollments = merchant?.enrollments || [];
                const enrollmentsToRestore = [];
                
                for (const original of originalEnrollments) {
                    const currentEnrollment = allCurrentEnrollments.find(current => 
                        current.providerId === original.providerId && 
                        current.serviceId === original.serviceId
                    );
                    
                    if (!currentEnrollment || currentEnrollment.active === false) {
                        enrollmentsToRestore.push(original);
                    }
                }
                
                if (enrollmentsToRestore.length > 0) {
                    const result = await Swal.fire({
                        title: 'Unsaved Changes',
                        text: `You've unenrolled ${enrollmentsToRestore.length} service(s). Do you want to revert these changes?`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Yes, revert changes',
                        cancelButtonText: 'No, keep changes'
                    });
                    
                    if (result.isConfirmed) {
                        Swal.fire({
                            title: 'Restoring enrollments...',
                            allowOutsideClick: false,
                            didOpen: () => {
                                Swal.showLoading();
                            }
                        });
                        
                        let successCount = 0;
                        let failCount = 0;
                        
                        for (const enrollment of enrollmentsToRestore) {
                            try {
                                const response = await axios.post(
                                    `${OI_URL}/api/v1/txshield/merchant/${merchantId}/enroll`,
                                    {
                                        providerId: enrollment.providerId,
                                        serviceId: enrollment.serviceId,
                                        client_legal_name: enrollment.client_legal_name || ""
                                    },
                                    { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
                                );
                                
                                if (response.data.status === "ok") {
                                    successCount++;
                                } else {
                                    failCount++;
                                }
                            } catch (error) {
                                failCount++;
                            }
                        }
                        
                        if (successCount > 0) {
                            await getMerchantDetail();
                            await fetchMerchants();
                        }
                        
                        Swal.close();
                        
                        await Swal.fire({
                            icon: successCount > 0 ? (failCount > 0 ? 'warning' : 'success') : 'error',
                            title: 'Enrollment Restoration',
                            text: `Successfully restored ${successCount} of ${enrollmentsToRestore.length} enrollment(s)${
                                failCount > 0 ? `. Failed to restore ${failCount} enrollment(s).` : ''
                            }`,
                            timer: 2000
                        });
                    } else {
                        const currentActiveEnrollments = allCurrentEnrollments.filter(e => e.active === true);
                        setOriginalEnrollments(JSON.parse(JSON.stringify(currentActiveEnrollments)));
                    }
                }
            }
            
            handleCancel();
        } catch (error) {
            try {
                await fetchMerchants();
            } catch (refreshError) {
                // Silently handle refresh errors
            }
            
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to process enrollment changes'
            });
            handleCancel();
        }
    };

    const onFinish = async (values) => {
        const { name, jwtToken, verifiMerchantName, verifiMid, enrollmentStatus, active } = values;
        const apiUrl = OI_URL + `/api/v1/txshield/merchant/${merchantId}/edit/`;
        
        try {
            const { data } = await axios.post(apiUrl, { 
                name, 
                jwtToken, 
                verifiMerchantName, 
                verifiMid, 
                enrollmentStatus, 
                active 
            }, { 
                headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } 
            });
            if (data.status === "ok") {
                Swal.fire({
                    icon: "success",
                    title: t("contents.oi.merchant_updated"),
                    showConfirmButton: false,
                    timer: 2000,
                    onClose: () => {
                        fetchMerchants();
                    },
                });
                handleCancel();
            } else {
                Swal.fire({
                    icon: "error",
                    title: t("login.dialogs.catch"),
                    text: data.msg ? data.msg : "",
                    showConfirmButton: true,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("login.dialogs.catch"),
                text: error.response ? error.response.data.msg : "",
                showConfirmButton: true,
            });
        }
        setLoading(false);
    };

    const getServiceName = async (serviceId) => {
        try {
            const { data } = await axios.get(
                `${OI_URL}/api/v1/txshield/provider_service/${serviceId}`,
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
            );
            setServiceNames(prev => ({
                ...prev,
                [serviceId]: data.serviceName
            }));
        } catch (error) {
            console.error('Error getting service name:', error);
        }
    };

    const getRdrRules = async (page = rdrRulesPage, pageSize = rdrRulesPageSize) => {
        const apiUrl = `${OI_URL}/api/v1/txshield/rdr/merchantrules/${merchantId}?page=${page}&pageSize=${pageSize}`;
        try {
            const response = await axios.get(
                apiUrl,
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } });
            setRdrRulesData(response.data.datalist);
            setRdrRulesTotal(response.data.total);
        } catch (error) {
            console.error('Error getting RDR rules:', error);
        }
    };

    const handleRdrRulesPaginationChange = (page, pageSize) => {
        setRdrRulesPage(page);
        setRdrRulesPageSize(pageSize);
        getRdrRules(page, pageSize);
    };

    useEffect(() => {
        if (merchant?.enrollments) {
            merchant.enrollments.forEach(enrollment => {
                if (enrollment.serviceId) {
                    getServiceName(enrollment.serviceId);
                }
            });
        }
        const rdrRules = merchant?.enrollments?.some(enrollment => 
            (enrollment.name.toLowerCase() === "verifi" && enrollment.service.toLowerCase() === "rdr decision")
        );
        setIsRdrRules(rdrRules);
        if (rdrRules && rdrRulesData.length === 0) {
            setRdrRulesPage(1);
            setRdrRulesPageSize(10);
            getRdrRules(1, 10);
        }
    }, [merchant]); // eslint-disable-line react-hooks/exhaustive-deps

    // // Function to handle editing the legal name
    // const handleEditLegalName = (record) => {
    //     setEditingEnrollment(record);
    //     setEditLegalName(record.client_legal_name || "");
    // };

    // Function to save the edited legal name
    // const saveLegalName = async () => {
    //     if (!editingEnrollment) return;
        
    //     try {
    //         const apiUrl = `${OI_URL}/api/v1/txshield/merchant/${merchantId}/enrollment/update`;
    //         const response = await axios.post(
    //             apiUrl,
    //             {
    //                 providerId: editingEnrollment.providerId,
    //                 serviceId: editingEnrollment.serviceId,
    //                 client_legal_name: editLegalName
    //             },
    //             { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
    //         );

    //         if (response.data.status === "ok") {
    //             Swal.fire({
    //                 icon: "success",
    //                 title: "Successfully updated legal name",
    //                 timer: 1500,
    //             });
    //             setEditingEnrollment(null);
    //             getMerchantDetail();
    //         } else {
    //             Swal.fire({
    //                 icon: "error",
    //                 title: "Error",
    //                 text: response.data.msg || "Failed to update legal name",
    //             });
    //         }
    //     } catch (error) {
    //         Swal.fire({
    //             icon: "error",
    //             title: "Error",
    //             text: error.response?.data?.msg || "Failed to update legal name",
    //         });
    //     }
    // };

    const enrollmentsColumns = [
        {
            title: "Provider",
            dataIndex: "name",
            key: "name",
            width: '20%'
        },
        {
            title: "Client Id",
            dataIndex: "clientId",
            key: "clientId",
            width: '20%'
        },
        {
            title: "Client Name",
            dataIndex: "client_legal_name",
            key: "client_legal_name",
            width: '20%',
            render: (text) => (text !== null && text !== undefined && text !== "")
                ? text
                : <span style={{ color: '#999' }}> </span>
        },
        {
            title: "Services",
            dataIndex: "service",
            key: "service",
            width: '20%',
            render: (_, record) => {
                if (record.name === 'ems') {
                    return <Tag color="blue">EMS</Tag>;
                }
    
                const provider = providerList.find(p => p.providerId === record.providerId);
                if (!provider?.services) return null;
    
                const serviceName = serviceNames[record.serviceId];
                const serviceInfo = provider.services.find(s => s.serviceId === serviceName);
                
                return serviceInfo && <Tag color="blue">{serviceInfo.serviceName}</Tag>;
            }
        },
        // {
        //     title: "Client Legal Name",
        //     dataIndex: "client_legal_name",
        //     key: "client_legal_name",
        //     width: '30%',
        //     render: (text, record) => {
        //         const isEditing = editingEnrollment && 
        //                           editingEnrollment.providerId === record.providerId && 
        //                           editingEnrollment.serviceId === record.serviceId;
                
        //         if (isEditing) {
        //             return (
        //                 <Space>
        //                     <Input 
        //                         value={editLegalName}
        //                         onChange={e => setEditLegalName(e.target.value)}
        //                         style={{ width: 180 }}
        //                     />
        //                     <Button 
        //                         type="primary" 
        //                         size="small" 
        //                         onClick={saveLegalName}
        //                     >
        //                         Save
        //                     </Button>
        //                     <Button 
        //                         size="small" 
        //                         onClick={() => setEditingEnrollment(null)}
        //                     >
        //                         Cancel
        //                     </Button>
        //                 </Space>
        //             );
        //         }
                
        //         return (text !== null && text !== undefined && text !== "") ? 
        //             text : 
        //             <span style={{color: '#999'}}>Not specified</span>;
        //     }
        // }
    ];
// Conditionally add the "Action" column for SYSADMIN users
if (PermissionsRequired("SYSADMIN") && !isReadOnly) {
    enrollmentsColumns.push({
        title: "Action",
        key: "action",
        width: '20%',
        render: (_, record) => (
            <Button
                type="link"
                style={{ color: record.active ? '#ff4d4f' : '#52c41a' }}
                onClick={() => (record.active ? handleUnenroll(record) : handleEnroll(record))}
            >
                {record.active ? 'Unenroll' : 'Enroll'}
            </Button>
        )
    });
}


    const handleEnroll = async (record) => {
        try {
            const apiUrl = `${OI_URL}/api/v1/txshield/merchant/${merchantId}/enroll`;
            const response = await axios.post(
                apiUrl,
                {
                    providerId: record.providerId,
                    serviceId: record.serviceId,
                    client_legal_name: record.client_legal_name || ""
                },
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
            );
    
            if (response.data.status === "ok") {
                Swal.fire({
                    icon: "success",
                    title: "Successfully enrolled",
                    timer: 1500,
                });
                getMerchantDetail();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.msg || "Failed to enroll",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.msg || "Failed to enroll",
            });
        }
    };
    
    const handleUnenroll = async (record) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Confirm Unenrollment",
            text: "Are you sure you want to unenroll from this service?",
            showCancelButton: true,
            confirmButtonText: "Yes, unenroll",
            cancelButtonText: "No, keep it"
        });
        
        if (!result.isConfirmed) {
            return;
        }
        
        try {
            const apiUrl = `${OI_URL}/api/v1/txshield/merchant/${merchantId}/${record.serviceId}/unenroll`;
            const response = await axios.post(
                apiUrl,
                {},
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
            );
    
            if (response.data.status === "ok") {
                Swal.fire({
                    icon: "success",
                    title: "Successfully unenrolled",
                    timer: 1500,
                });
                getMerchantDetail();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.msg || "Failed to unenroll",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.msg || "Failed to unenroll",
            });
        }
    };

    const handleProviderChange = (value) => {
        setSelectedProvider(value);
        setSelectedService(null);
    };

    const getAvailableServices = () => {
        const provider = providerList.find(p => p.providerId === selectedProvider);
        return provider?.services || [];
    };

    const handleNewEnrollment = async () => {
        if (!selectedProvider || !selectedService) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Please select both a provider and a service",
            });
            return;
        }
    
        try {
            const apiUrl = `${OI_URL}/api/v1/txshield/merchant/${merchantId}/enroll`;
            const response = await axios.post(
                apiUrl,
                {
                    providerId: selectedProvider,
                    serviceId: selectedService,
                    client_legal_name: clientLegalName
                },
                { headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } }
            );
    
            if (response.data.status === "ok") {
                Swal.fire({
                    icon: "success",
                    title: "Successfully added new enrollment",
                    timer: 1500,
                });
                getMerchantDetail();
                setClientLegalName("");
                setSelectedProvider(null);
                setSelectedService(null);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response.data.msg || "Failed to add enrollment",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.msg || "Failed to add enrollment",
            });
        }
    };

    // RDR Rules table columns
    const rdrRulesColumns = [
        {
            title: 'BIN',
            dataIndex: 'bin',
            key: 'bin',
        },
        {
            title: 'CAID',
            dataIndex: 'caid',
            key: 'caid',
        },
        {
            title: 'MCC',
            dataIndex: 'mcc',
            key: 'mcc',
        },
        {
            title: 'Template Name',
            dataIndex: 'templateName',
            key: 'templateName',
        },
        {
            title: 'Effective Date',
            dataIndex: 'effectiveDate',
            key: 'effectiveDate',
            render: (value) => {
                return value ? moment(value).format('MM/DD/YYYY') : '-';
            }
        },
        {
            title: 'Decision Template',
            dataIndex: 'rules',
            key: 'rules',
            render: (value) => {
              if (Array.isArray(value)) {
                const rules = value.map(rule => rule.ruleName);
                return rules.join(', ');
              }
              return '-';
            }
          }
    ];

    return (
        <Modal
            title="View Merchant"
            visible={isModalVisible}
            width={900}
            onOk={handleOk}
            onCancel={handleCancelWithRestore}
            footer={[
                <Button key="back" onClick={handleCancelWithRestore}>
                {t("contents.chargebacks.form.cancel")}
                </Button>,
                PermissionsRequired("SYSADMIN") && !isReadOnly && (
                <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
                    {loading
                    ? t("contents.chargebacks.form.submitting")
                    : t("contents.chargebacks.form.submit")}
                </Button>
                ),
            ]}
            >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Tabs defaultActiveKey="1">
                    <TabPane tab="General" key="1">
                        <Form.Item name="merchantId" label="Merchant ID">                    
                            <Input disabled />
                        </Form.Item>
                        
                        <Form.Item 
                            name="name" 
                            label={t("contents.oi.form.name")} 
                        >
                            <Input disabled={isReadOnly} />
                        </Form.Item>
                        
                        <Form.Item 
                            name="jwtToken" 
                            label={t("contents.oi.form.jwt_token")} 
                        >
                            <TextArea rows={3} readOnly disabled={isReadOnly} />
                        </Form.Item>
                        
                        <Form.Item 
                            name="active" 
                            label={t("contents.oi.form.active")} 
                        >
                            <Select disabled={isReadOnly} placeholder="Select status">
                                <Option value={true}>Active</Option>
                                <Option value={false}>Inactive</Option>
                            </Select>
                        </Form.Item>
                        
                        {isReadOnly ? null : 
                            <Form.Item>
                                <Button onClick={generateJWTToken}>
                                    {t("contents.oi.form.generate_jwt")}
                                </Button>
                            </Form.Item>
                        }

                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="mb-0">Enrollments</h4>
                                {PermissionsRequired("SYSADMIN") && !isReadOnly ? <Space direction="vertical" style={{ width: '100%' }}>
                                 <Space>
                                        <Select
                                            placeholder="Select Provider"
                                            style={{ width: 180 }}
                                            value={selectedProvider}
                                            onChange={handleProviderChange}
                                        >
                                            {providerList.map(provider => (
                                                <Option key={provider.providerId} value={provider.providerId}>
                                                    {provider.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        <Select
                                            placeholder="Select Service"
                                            style={{ width: 180 }}
                                            value={selectedService}
                                            onChange={value => setSelectedService(value)}
                                            disabled={!selectedProvider}
                                        >
                                            {getAvailableServices().map(service => (
                                                <Option key={service.serviceId} value={service.serviceId}>
                                                    {service.serviceName}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Space>
                                    
                                    <Space style={{ width: '100%' }}>
                                        <Input 
                                            placeholder="Client Legal Name" 
                                            value={clientLegalName}
                                            onChange={e => setClientLegalName(e.target.value)}
                                            style={{ width: 300 }}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            disabled={!selectedProvider || !selectedService || !clientLegalName} 
                                            onClick={handleNewEnrollment}
                                        >
                                            Enroll
                                        </Button>
                                    </Space>
                                </Space>:null}
                            </div>
                            
                            {merchant?.enrollments?.length > 0 ? (
                                <Table
                                    columns={enrollmentsColumns}
                                    dataSource={merchant.enrollments}
                                    rowKey={record => `${record.providerId}-${record.serviceId}`}
                                    pagination={false}
                                    size="small"
                                />
                            ) : (
                                <Alert
                                    message="No enrollments found"
                                    type="info"
                                    showIcon
                                />
                            )}
                        </div>
                    </TabPane>
                    {isRdrRules && (
                        <TabPane tab="RDR Decision" key="2">
                             <Table
                                 columns={rdrRulesColumns}
                                 dataSource={rdrRulesData}
                                 rowKey="bincaidId"
                                 pagination={{
                                     current: rdrRulesPage,
                                     total: rdrRulesTotal,
                                     pageSize: rdrRulesPageSize,
                                     showSizeChanger: true,
                                     showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                     onChange: handleRdrRulesPaginationChange,
                                     onShowSizeChange: handleRdrRulesPaginationChange
                                 }}
                                 size="small"
                                 scroll={{ x: 800 }}
                             />
                            {rdrRulesData.length === 0 && (
                                <Alert
                                    message="No RDR rules found"
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            )}
                        </TabPane>
                    )}
                </Tabs>
            </Form>
        </Modal>
    );
};

export default MerchantModal;